import { BANNED_URLS, PAGE_TYPES } from '../constants/assistant-config';

export const getPortalRootNode = () => {
  return document?.querySelector('#root');
};

export const getStickyATCElement = () => {
  // Query elements matching the markup added by the Sticky component
  const buyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:Buybox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const applianceBuyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:MajorApplianceBuyBox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxActionsMatch = document.querySelector(
    'div[data-component^=buybox\\:BuyboxActions\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxSimpleMatch = document.querySelector(
    'div[data-component^=Buybox] [style^=min-height] > [class*=sticky]'
  );

  // Use the more specific section if available
  if (buyboxActionsMatch) return buyboxActionsMatch;

  if (applianceBuyboxMatch) return applianceBuyboxMatch;

  if (buyboxSimpleMatch) return buyboxSimpleMatch;

  // Use the generic (older) version as fallback
  if (buyboxMatch) return buyboxMatch;

  return null;
};

export const getPath = () => {
  return typeof window !== 'undefined' ? new URL(window.document.documentURI).pathname : '';
};

// Frozen. This classifier exists only to keep getIsLegacyCompatiblePage()
// behaving exactly as it always has; nothing else should use it.
export const getLegacyPageType = () => {
  const pathArray = getPath()?.split('/') || [];

  if (pathArray[1] === 'c') return PAGE_TYPES.CONTENT;
  if (pathArray[1] === 'p') {
    if (pathArray[2] === 'sets') return PAGE_TYPES.BIP;
    return PAGE_TYPES.PIP;
  }
  if (pathArray[1] === 's') return PAGE_TYPES.SEARCH;
  if (pathArray[1] === 'b') return PAGE_TYPES.BROWSE;
  if (pathArray[1] === '') return PAGE_TYPES.HOME;

  return PAGE_TYPES.UNKNOWN;
};

// Fallback for the window between page load and analytics initialising, and for
// any experience that never sets digitalData. Mirrors getPageType() in
// thd-analytics (src/components/thd-analytics-utils.js). Order is significant:
// narrower rules first, exactly as in the source.
const PAGE_TYPE_RULES = [
  [/^\/s\//, 'search'],
  [/^\/b\/.*Ntt-/, 'search'],
  [/^\/p\/paint\/config\//, 'magic pip'],
  [/^\/p\/rental\/reservations\//, 'tool:productrental:reservation'],
  [/^\/p\/rental\//, 'tool:productrental:pip'],
  [/^\/p\/sets/, 'bip'],
  [/^\/p\//, 'pip'],
  [/^\/pep\//, 'pep'],
  [/^\/b\//, 'browse'],
  [/^\/c\//, 'content'],
  [/^\/l\//, 'storefinder'],
  [/^\/collection\//, 'collection'],
  [/^\/compare\//, 'compare'],
  [/^\/buyitagain/, 'buy it again page'],
  [/^\/list\/view\/details\/shared\//, 'list details>shared'],
  [/^\/list\/view\/details\//, 'list details'],
  [/^\/gift-cards\//, 'giftcard'],
  [/^\/cart(\/|$)/, 'cart'],
  [/^\/bath-renovation/, 'bathroom design studio'],
  [/^\/configurator-projects/, 'flooring projects'],
  [/^\/projects\/.+/, 'project details'],
  [/^\/projects\/?$/, 'projects'],
  [/^\/?$/, 'homepage'],
];

const pageTypeFromPath = (path = '') => {
  const normalised = path.replace(/^\/render[^/]*/, '');
  return PAGE_TYPE_RULES.find(([pattern]) => pattern.test(normalised))?.[1] ?? 'unknown';
};

// The page type the site already reports to analytics — the single value used
// everywhere outside getIsLegacyCompatiblePage(). Read digitalData rather than
// window.ddo: ddo is an alias present on some experiences only (defined on /p/*,
// undefined on /b/*, /s/*, /c/*, /l/*), while digitalData was set on every page
// checked. Values it returns are the site's own vocabulary and are open-ended
// (tool:*, maml:*, collections:*), so never switch on an exhaustive list.
export const getCanonicalPageType = () => {
  return window?.digitalData?.page?.pageInfo?.pageType || pageTypeFromPath(getPath());
};

export const isSessionExpired = (sessionTimestamp) => {
  // If no timestamp, consider it expired
  if (!sessionTimestamp) {
    return true;
  }

  const now = Date.now();
  const sessionAge = now - sessionTimestamp;

  // Session expires after 24 hours
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

  return sessionAge > TWENTY_FOUR_HOURS;
};

export const getSessionId = () => {
  const FALLBACK_SESSION_PREFIX = 'fallback-sessionId';

  if (typeof window !== 'undefined') {
    const cookieSessionId = window?.cookieUtils?.readBrowserCookie('thda.s');
    if (cookieSessionId) {
      return cookieSessionId;
    }
    const uuid = window?.crypto?.randomUUID?.() || Date.now();
    return `${FALLBACK_SESSION_PREFIX}-${uuid}`;
  }

  return `${FALLBACK_SESSION_PREFIX}-${Date.now()}`;
};

export const createActionId = (sessionId) => {
  if (!sessionId) {
    return `${crypto.randomUUID()}_${Date.now().toString()}`;
  }
  return `${sessionId}_${Date.now().toString()}`;
};

export const getDefaultSession = () => ({
  sessionId: getSessionId(),
  timestamp: Date.now(),
  shouldAutoOpen: false,
  hasSessionInteractedWithFab: false,
  pagesExpandedFABShownOn: [],
  isEscalated: false
});

export const getIsCompatiblePage = () => {
  if (BANNED_URLS.includes(getPath())) {
    return false;
  }

  return [
    PAGE_TYPES.HOME,
    PAGE_TYPES.PIP,
    PAGE_TYPES.BROWSE,
    PAGE_TYPES.SEARCH,
    PAGE_TYPES.CONTENT,
  ].includes(getLegacyPageType());
};

export const waitForIdle = (callback, timeout = 3000) => {
  const run = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout });
    } else {
      // fallback for Safari/older browsers
      setTimeout(callback, 0);
    }
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
};

export const HOST_CONSTANTS = {
  // eslint-disable-next-line
  DEFAULT_HOST: typeof import.meta.env.MAA__DEFAULT_HOST__ !== 'undefined' ?  import.meta.env.MAA__DEFAULT_HOST__ : '',
  GRAPHQL_HOST: {
    DEV: 'https://apionline-dev.homedepotdev.com',
    STAGE: 'https://apionline-stage.homedepotdev.com',
    PROD: 'https://apionline.homedepot.com'
  },
  ENV: {
    DEV: 'hd-qa71',
    STAGE: 'hd-qa74',
    LOCAL: 'vite'
  }
};

export const getGraphqlHost = () => {
  const currentHost = typeof window !== 'undefined' ? window?.location?.host?.split('.')[0] : null;
  const { ENV, GRAPHQL_HOST, DEFAULT_HOST } = HOST_CONSTANTS;
  const LOCAL_ENV = import.meta.env.mode;

  switch (currentHost) {
  case ENV.DEV: {
    return GRAPHQL_HOST.DEV;
  }
  case ENV.STAGE: {
    return GRAPHQL_HOST.STAGE;
  }
  case ENV.LOCAL: {
    return DEFAULT_HOST;
  }
  default: {
    return GRAPHQL_HOST.PROD;
  }
  }
};